/**
* Localized rich content for /(locale)/services pages.
* Sprint — Rank Push Wave 6: expand thin 60-line locale routes into
* A5 quality pages with intro, why-us, fee guide, FAQ + FAQPage schema
* targeting locale-specific SERPs.
*/
export type ServicesLocale = "zh" | "ja" | "ko" | "vi" | "id";

export interface ServicesLocaleContent {
  title: string;
  description: string;
  keywords: string;
  homeLabel: string;
  servicesLabel: string;
  eyebrow: string;
  heroTitle: string;
  heroSubtitle: string;
  answerSummaryLabel: string;
  answerSummary: string;
  aboutHeading: string;
  intro: string;
  whyHeading: string;
  why: string[];
  feeGuideLabel: string;
  fees: { label: string; price: string }[];
  ctaQuote: string;
  ctaCall: string;
  ctaLine: string;
  categoriesHeading: string;
  faqHeading: string;
  faq: { q: string; a: string }[];
  relatedHeading: string;
  related: { label: string; to: string }[];
  finalEyebrow: string;
  finalHeading: string;
  badges: string[];
}

export const SERVICES_LOCALE: Record<ServicesLocale, ServicesLocaleContent> = {
  zh: {
    title: "泰国公证服务 — 认证翻译与外交部使馆认证 | Thai Notary Law",
    description:
      "曼谷持证公证律师事务所。公证、副本认证、7语种认证翻译、泰国外交部及大使馆全链认证、签证与公司法律服务。当日办理，收费透明，500泰铢起。",
    keywords:
      "泰国公证, 曼谷公证律师, 泰国认证翻译, 泰国外交部认证, 泰国大使馆认证, 泰国海牙认证, apostille thailand, notary public bangkok",
    homeLabel: "中文首页",
    servicesLabel: "服务",
    eyebrow: "中文 · 完整服务目录",
    heroTitle: "泰国公证 · 认证翻译 · 外交部与大使馆认证",
    heroSubtitle: "16大类 · 135+项服务 · 6位持证公证律师 · 7种工作语言 · 曼谷当日办结",
    answerSummaryLabel: "问答摘要",
    answerSummary:
      "Thai Notary Law & Service 有限公司为曼谷持证律师事务所，提供公证、副本认证、认证翻译，以及泰国外交部与目的国大使馆的全链认证（含 2027 年 2 月 28 日生效的海牙认证）。6 位律师均持有泰国律师协会颁发的公证律师执照（2551 佛历规则）。收费自 500 泰铢/章起，曼谷标准业务当日完成，我方直接前往外交部（Chaeng Watthana）代办。",
    aboutHeading: "关于事务所",
    intro:
      "Thai Notary Law & Service 有限公司总部位于曼谷（Ekamai），并在孔敬、乌隆他尼、廊开设有分所，主要为在泰外籍人士、跨国企业及泰国出海企业提供公证、认证翻译与国际文书认证服务。团队 6 位公证律师合计执业经验超过 60 年，长期服务于各国驻曼谷大使馆、海外大学、外国法院及世界 500 强法务部门。",
    whyHeading: "国际客户的选择",
    why: [
      "6 位持证公证律师（泰国律师协会 2551 佛历规则）",
      "工作语言 7 种：泰、英、中、日、韩、越、印尼",
      "4 家实体分所：曼谷总部 · 孔敬 · 乌隆他尼 · 廊开",
      "标准公证当日完成；24 小时加急认证可预约",
      "外交部（Chaeng Watthana）代排队代办",
      "官网公示固定收费 · 出具增值税发票",
      "曼谷 50 区上门服务（酒店、公寓、办公室）",
      "2027 年 2 月 28 日起支持海牙认证（Apostille）",
    ],
    feeGuideLabel: "收费一览",
    fees: [
      { label: "签名认证", price: "500 泰铢起" },
      { label: "副本认证", price: "500 泰铢起" },
      { label: "认证翻译", price: "300 泰铢/页起" },
      { label: "外交部+使馆全链", price: "5,000 泰铢起" },
    ],
    ctaQuote: "获取报价",
    ctaCall: "📞 +66-94-895-8999",
    ctaLine: "💬 LINE @Thainotary",
    categoriesHeading: "全部 16 大服务类别",
    faqHeading: "常见问题",
    faq: [
      {
        q: "在泰国办理公证需要多少费用？",
        a: "本所公证费自 500 泰铢/章起（签名认证或副本认证）。认证翻译自 300 泰铢/页起。公证+外交部+使馆全链认证按国家报价，一般 3–7 个工作日完成。",
      },
      {
        q: "你们的公证是否被各国大使馆及海外机构认可？",
        a: "是。6 位律师全部持有泰国律师协会颁发的公证律师执照（2551 佛历规则）。认证文书获曼谷所有大使馆、泰国外交部（Chaeng Watthana）以及海外法院、大学、银行认可。",
      },
      {
        q: "可以在泰国办理护照、学历、身份证的副本认证吗？",
        a: "可以。副本认证（Certified True Copy）是曼谷办理量最大的项目之一，常见文书包括护照、身份证、出生证、结婚证、学位证、成绩单、公司注册证书等。当日出件，曼谷总部支持现场办理。",
      },
      {
        q: "境外使用的文书如何办理认证（海牙 / 使馆链）？",
        a: "全链代办：公证 → 泰国外交部认证 → 目的国大使馆认证。泰国自 2027 年 2 月 28 日起加入海牙认证公约，多数目的地已只需 Apostille，可显著缩短时间与成本。我们会按国家给出正确流程。",
      },
      {
        q: "是否提供中文（普通话）服务？",
        a: "提供。团队支持中文（普通话）、英文、日文、韩文、越南文、印尼文与泰文的公证与认证翻译。",
      },
      {
        q: "公证律师可以到曼谷酒店 / 公寓 / 办公室吗？",
        a: "可以。曼谷 50 区（Sathorn、Silom、Sukhumvit、Asoke、Phrom Phong、Thonglor、Rama 9 等）均可上门。请至少提前一个工作日通过 LINE @Thainotary 或 +66-94-895-8999 预约。",
      },
    ],
    relatedHeading: "相关页面",
    related: [
      { label: "中文首页", to: "/zh" },
      { label: "服务覆盖", to: "/zh/coverage" },
      { label: "关于我们", to: "/zh/about" },
      { label: "联系方式", to: "/zh/contact" },
    ],
    finalEyebrow: "立即开始",
    finalHeading: "免费咨询 · 透明报价 · 无隐藏费用",
    badges: ["泰国律师协会认证", "外交部+使馆代办", "当日办结"],
  },

  ja: {
    title: "タイ公証サービス — 認証翻訳・外務省・大使館認証 | Thai Notary Law",
    description:
      "バンコクの登録公証弁護士事務所。公証、写し認証、7言語対応の認証翻訳、タイ外務省+大使館フルチェーン認証、ビザ・会社法務。当日対応、料金明朗、500バーツから。",
    keywords:
      "タイ 公証, バンコク 公証, タイ 認証翻訳, タイ 外務省認証, タイ アポスティーユ, apostille thailand, notary public bangkok",
    homeLabel: "日本語トップ",
    servicesLabel: "サービス",
    eyebrow: "日本語 · 全サービス一覧",
    heroTitle: "タイ公証 · 認証翻訳 · 外務省+大使館認証",
    heroSubtitle: "16カテゴリ · 135+サービス · 公証弁護士6名 · 7言語対応 · バンコク当日仕上げ",
    answerSummaryLabel: "回答サマリー",
    answerSummary:
      "Thai Notary Law & Service Co., Ltd. はバンコク拠点の登録法律事務所で、公証、写し認証、認証翻訳、タイ外務省および目的国大使館までのフルチェーン認証（2027年2月28日発効のアポスティーユ対応）を提供します。所属6名の弁護士は全員、タイ弁護士協会の公証弁護士資格（2551仏暦規則）を保持。料金は1押印500バーツから、バンコクの標準案件は当日仕上げ、外務省（Chaeng Watthana）への提出も代行します。",
    aboutHeading: "事務所について",
    intro:
      "Thai Notary Law & Service Co., Ltd. はバンコク本部（エカマイ）に加え、コーンケン、ウドンタニ、ノーンカーイに支店を有し、在タイ日本人・多国籍企業・海外進出タイ企業向けに公証・認証翻訳・国際文書認証を提供しています。公証弁護士6名は合計60年超の経験を持ち、各国大使館、海外大学、外国裁判所、Fortune 500の法務部門に長年サービスを提供しています。",
    whyHeading: "国際クライアントに選ばれる理由",
    why: [
      "登録公証弁護士6名（タイ弁護士協会 2551仏暦規則）",
      "対応言語7ヶ国語：タイ・英・中・日・韓・越・インドネシア",
      "実店舗4拠点：バンコク本部 · コーンケン · ウドンタニ · ノーンカーイ",
      "標準案件は当日仕上げ／24時間エクスプレス認証対応",
      "外務省（Chaeng Watthana）への提出を代行",
      "サイト上に固定料金を明示／VATインボイス発行",
      "バンコク全50区で出張対応（ホテル・コンド・オフィス）",
      "2027年2月28日よりアポスティーユ対応",
    ],
    feeGuideLabel: "料金の目安",
    fees: [
      { label: "署名認証", price: "500バーツ〜" },
      { label: "写し認証", price: "500バーツ〜" },
      { label: "認証翻訳", price: "300バーツ/頁〜" },
      { label: "外務省+大使館フル", price: "5,000バーツ〜" },
    ],
    ctaQuote: "見積を依頼",
    ctaCall: "📞 +66-94-895-8999",
    ctaLine: "💬 LINE @Thainotary",
    categoriesHeading: "全16サービスカテゴリ",
    faqHeading: "よくある質問",
    faq: [
      {
        q: "タイでの公証費用はいくらですか？",
        a: "当所の公証料は1押印500バーツから（署名認証／写し認証）。認証翻訳は300バーツ/頁から。公証+外務省+大使館のフルチェーンは国別見積で、通常3〜7営業日で完了します。",
      },
      {
        q: "貴所の公証は各国大使館・海外機関で認められますか？",
        a: "はい。所属弁護士6名は全員、タイ弁護士協会の公証弁護士資格（2551仏暦規則）を保持。認証文書はバンコクの全大使館、タイ外務省（Chaeng Watthana）、および海外の裁判所・大学・銀行で受理されます。",
      },
      {
        q: "パスポート・卒業証書・IDの写し認証は可能ですか？",
        a: "可能です。写し認証（Certified True Copy）はバンコクで最も需要の高いサービスで、パスポート・在留カード・出生証明書・婚姻証明書・卒業証書・成績証明書・会社登記簿などが対象。当日仕上げ、本部での飛び込み対応も可能です。",
      },
      {
        q: "海外使用文書の認証（アポスティーユ／領事チェーン）は可能ですか？",
        a: "可能です。公証→タイ外務省認証→目的国大使館認証までフル代行。タイは2027年2月28日よりハーグ条約に加盟し、多くの国向けはアポスティーユのみで完結、時間とコストを大幅削減できます。国別に正しいフローをご案内します。",
      },
      {
        q: "日本語での対応は可能ですか？",
        a: "可能です。日本語・英語・中国語・韓国語・ベトナム語・インドネシア語・タイ語で、公証と認証翻訳の両方に対応しています。",
      },
      {
        q: "バンコクのホテル・コンド・オフィスへの出張は可能ですか？",
        a: "可能です。バンコク全50区（サトーン・シーロム・スクンビット・アソーク・プロンポン・トンロー・ラマ9・ラーチャダ等）で出張対応。LINE @Thainotary または +66-94-895-8999 で前営業日までにご予約ください。",
      },
    ],
    relatedHeading: "関連ページ",
    related: [
      { label: "日本語トップ", to: "/ja" },
      { label: "対応エリア", to: "/ja/coverage" },
      { label: "事務所紹介", to: "/ja/about" },
      { label: "お問い合わせ", to: "/ja/contact" },
    ],
    finalEyebrow: "今すぐ開始",
    finalHeading: "無料相談 · 明朗見積 · 追加費用なし",
    badges: ["タイ弁護士協会登録", "外務省+大使館代行", "当日仕上げ"],
  },

  ko: {
    title: "태국 공증 서비스 — 인증번역·외무부·대사관 인증 | Thai Notary Law",
    description:
      "방콕 등록 공증변호사 사무소. 공증, 사본 인증, 7개 언어 인증번역, 태국 외무부+대사관 풀체인 인증, 비자 및 회사 법무. 당일 처리, 투명 요금, 500바트부터.",
    keywords:
      "태국 공증, 방콕 공증, 태국 인증번역, 태국 외무부 인증, 태국 아포스티유, apostille thailand, notary public bangkok",
    homeLabel: "한국어 홈",
    servicesLabel: "서비스",
    eyebrow: "한국어 · 전체 서비스",
    heroTitle: "태국 공증 · 인증번역 · 외무부+대사관 인증",
    heroSubtitle: "16개 카테고리 · 135+개 서비스 · 공증변호사 6명 · 7개 언어 · 방콕 당일 처리",
    answerSummaryLabel: "답변 요약",
    answerSummary:
      "Thai Notary Law & Service Co., Ltd.는 방콕 소재 등록 법무법인으로 공증, 사본 인증, 인증번역, 태국 외무부 및 목적국 대사관까지의 풀체인 인증(2027년 2월 28일 발효 아포스티유 포함)을 제공합니다. 소속 변호사 6명 전원 태국 변호사협회 공증변호사 자격(2551 불력 규정) 보유. 요금은 도장 1건 500바트부터, 방콕 표준 건은 당일 완료, 외무부(Chaeng Watthana) 제출도 대행합니다.",
    aboutHeading: "사무소 소개",
    intro:
      "Thai Notary Law & Service Co., Ltd.는 방콕 본사(에까마이)와 콘깬·우돈타니·농카이 지사를 운영하며, 재태 한국인·다국적 기업·해외 진출 태국 기업을 대상으로 공증·인증번역·국제 문서 인증을 제공합니다. 공증변호사 6명의 총 실무 경력은 60년 이상이며, 각국 방콕 주재 대사관, 해외 대학, 외국 법원, 포춘 500 법무팀에 서비스를 제공해 왔습니다.",
    whyHeading: "국제 고객이 선택하는 이유",
    why: [
      "공증변호사 6명 (태국 변호사협회 2551 불력 규정)",
      "7개 언어 대응: 태·영·중·일·한·베·인니",
      "실체 지점 4곳: 방콕 본사·콘깬·우돈타니·농카이",
      "표준 건 당일 처리, 24시간 특급 인증 예약 가능",
      "외무부(Chaeng Watthana) 대기·제출 대행",
      "웹사이트에 고정 요금 공개, VAT 세금계산서 발행",
      "방콕 50개 구 전지역 출장 서비스",
      "2027년 2월 28일부터 아포스티유 대응",
    ],
    feeGuideLabel: "요금 안내",
    fees: [
      { label: "서명 인증", price: "500바트~" },
      { label: "사본 인증", price: "500바트~" },
      { label: "인증번역", price: "300바트/장~" },
      { label: "외무부+대사관 풀", price: "5,000바트~" },
    ],
    ctaQuote: "견적 요청",
    ctaCall: "📞 +66-94-895-8999",
    ctaLine: "💬 LINE @Thainotary",
    categoriesHeading: "전체 16개 서비스 카테고리",
    faqHeading: "자주 묻는 질문",
    faq: [
      {
        q: "태국 공증 비용은 얼마인가요?",
        a: "본 법무법인의 공증 요금은 도장 1건당 500바트부터입니다(서명 인증/사본 인증). 인증번역은 300바트/장부터. 공증+외무부+대사관 풀체인은 국가별 견적, 통상 3~7영업일 소요됩니다.",
      },
      {
        q: "귀 사무소의 공증은 대사관 및 해외 기관에서 인정되나요?",
        a: "네. 변호사 6명 전원이 태국 변호사협회의 공증변호사 자격(2551 불력 규정)을 보유합니다. 인증 문서는 방콕 소재 모든 대사관, 태국 외무부(Chaeng Watthana), 해외 법원·대학·은행에서 인정됩니다.",
      },
      {
        q: "여권·학위증·신분증의 사본 인증이 가능한가요?",
        a: "가능합니다. 사본 인증(Certified True Copy)은 방콕에서 가장 수요가 많은 서비스로, 여권·신분증·출생증명서·혼인관계증명서·학위증·성적증명서·법인 등기부 등본 등이 대상입니다. 당일 처리, 본사 방문 접수 가능.",
      },
      {
        q: "해외 사용 문서의 인증(아포스티유/영사 체인)이 가능한가요?",
        a: "가능합니다. 공증→태국 외무부 인증→목적국 대사관 인증까지 풀 대행. 태국은 2027년 2월 28일부터 헤이그 협약에 가입, 다수 국가는 아포스티유만으로 완결되어 시간과 비용이 크게 절감됩니다. 국가별로 정확한 절차를 안내해 드립니다.",
      },
      {
        q: "한국어 응대가 가능한가요?",
        a: "가능합니다. 한국어·영어·중국어·일본어·베트남어·인도네시아어·태국어로 공증과 인증번역 모두 지원합니다.",
      },
      {
        q: "방콕 호텔·콘도·사무실 출장이 가능한가요?",
        a: "가능합니다. 방콕 50개 구 전지역(사톤·씰롬·수쿰빗·아속·프롬퐁·통로·라마9·라차다 등) 출장. LINE @Thainotary 또는 +66-94-895-8999로 전영업일 예약 부탁드립니다.",
      },
    ],
    relatedHeading: "관련 페이지",
    related: [
      { label: "한국어 홈", to: "/ko" },
      { label: "대응 지역", to: "/ko/coverage" },
      { label: "사무소 소개", to: "/ko/about" },
      { label: "문의하기", to: "/ko/contact" },
    ],
    finalEyebrow: "지금 시작",
    finalHeading: "무료 상담 · 투명 견적 · 추가 비용 없음",
    badges: ["태국 변호사협회 등록", "외무부+대사관 대행", "당일 처리"],
  },

  vi: {
    title: "Dịch vụ công chứng Thái Lan — Dịch thuật công chứng & Hợp pháp hóa | Thai Notary Law",
    description:
      "Văn phòng luật sư công chứng có giấy phép tại Bangkok. Công chứng, chứng thực bản sao, dịch thuật công chứng 7 ngôn ngữ, hợp pháp hóa Bộ Ngoại giao Thái + Đại sứ quán, visa & luật doanh nghiệp. Xử lý trong ngày, phí minh bạch — liên hệ để nhận báo giá.",
    keywords:
      "công chứng thái lan, luật sư công chứng bangkok, dịch thuật công chứng thái lan, hợp pháp hóa lãnh sự thái lan, apostille thailand, notary public bangkok",
    homeLabel: "Trang chủ",
    servicesLabel: "Dịch vụ",
    eyebrow: "Tiếng Việt · Toàn bộ dịch vụ",
    heroTitle: "Công chứng Thái Lan · Dịch công chứng · Hợp pháp hóa lãnh sự",
    heroSubtitle: "16 danh mục · 135+ dịch vụ · 6 luật sư công chứng có giấy phép · 7 ngôn ngữ · Xử lý trong ngày tại Bangkok",
    answerSummaryLabel: "Tóm tắt câu trả lời",
    answerSummary:
      "Thai Notary Law & Service Co., Ltd. là văn phòng luật có giấy phép tại Bangkok, cung cấp công chứng, chứng thực bản sao, dịch thuật công chứng và hợp pháp hóa trọn gói (Công chứng → Bộ Ngoại giao Thái → Đại sứ quán, có Apostille từ 28/02/2027). Cả 6 luật sư đều có chứng chỉ Luật sư Công chứng của Hiệp hội Luật sư Thái Lan (Quy định 2551 Phật lịch). Phí dịch vụ tùy theo loại giấy tờ, liên hệ để nhận báo giá; xử lý trong ngày tại Bangkok, chúng tôi trực tiếp nộp tại Bộ Ngoại giao (Chaeng Watthana).",
    aboutHeading: "Về văn phòng",
    intro:
      "Thai Notary Law & Service Co., Ltd. có trụ sở tại Bangkok (Ekkamai) và chi nhánh tại Khon Kaen, Udon Thani, Nong Khai, chuyên phục vụ người Việt tại Thái, doanh nghiệp đa quốc gia và các công ty Thái Lan có giao dịch quốc tế. Đội ngũ 6 Luật sư Công chứng có tổng cộng hơn 60 năm kinh nghiệm, đã phục vụ các đại sứ quán tại Bangkok, đại học nước ngoài, tòa án nước ngoài và bộ phận pháp chế của các tập đoàn Fortune 500.",
    whyHeading: "Vì sao khách hàng quốc tế chọn chúng tôi",
    why: [
      "6 luật sư công chứng có giấy phép (Hiệp hội Luật sư Thái Lan, Quy định 2551 PL)",
      "7 ngôn ngữ làm việc: Thái, Anh, Trung, Nhật, Hàn, Việt, Indonesia",
      "4 chi nhánh thực tế: Bangkok · Khon Kaen · Udon Thani · Nong Khai",
      "Dịch vụ trong ngày cho các thủ tục tiêu chuẩn; hợp pháp hóa hỏa tốc 24h",
      "Trực tiếp nộp hồ sơ tại Bộ Ngoại giao (Chaeng Watthana), khách không cần chờ",
      "Bảng giá cố định công khai, xuất hóa đơn VAT",
      "Dịch vụ tận nơi tại toàn bộ 50 quận Bangkok (khách sạn, chung cư, văn phòng)",
      "Áp dụng Công ước Apostille từ 28/02/2027",
    ],
    feeGuideLabel: "Bảng phí tham khảo",
    fees: [
      { label: "Chứng thực chữ ký", price: "Liên hệ báo giá" },
      { label: "Chứng thực bản sao", price: "Liên hệ báo giá" },
      { label: "Dịch thuật công chứng", price: "Báo giá theo trang" },
      { label: "Trọn gói BNG + ĐSQ", price: "Liên hệ báo giá" },
    ],
    ctaQuote: "Yêu cầu báo giá",
    ctaCall: "📞 +66-94-895-8999",
    ctaLine: "💬 LINE @Thainotary",
    categoriesHeading: "Toàn bộ 16 danh mục dịch vụ",
    faqHeading: "Câu hỏi thường gặp",
    faq: [
      {
        q: "Phí công chứng tại Thái Lan là bao nhiêu?",
        a: "Phí công chứng (chứng thực chữ ký hoặc chứng thực bản sao) và dịch thuật công chứng tùy theo loại giấy tờ — liên hệ đội ngũ của chúng tôi để nhận báo giá. Trọn gói Công chứng + Bộ Ngoại giao + Đại sứ quán được báo giá theo quốc gia, thường hoàn thành trong 3–7 ngày làm việc.",
      },
      {
        q: "Công chứng của quý văn phòng có được đại sứ quán và cơ quan nước ngoài công nhận không?",
        a: "Có. Cả 6 luật sư đều có chứng chỉ Luật sư Công chứng của Hiệp hội Luật sư Thái Lan (Quy định 2551 PL). Văn bản được toàn bộ đại sứ quán tại Bangkok, Bộ Ngoại giao Thái Lan (Chaeng Watthana), tòa án, đại học và ngân hàng nước ngoài công nhận.",
      },
      {
        q: "Có thể chứng thực bản sao hộ chiếu, bằng cấp, CCCD tại Thái Lan không?",
        a: "Có. Chứng thực bản sao (Certified True Copy) là dịch vụ được yêu cầu nhiều nhất tại Bangkok. Bao gồm hộ chiếu, CCCD, giấy khai sinh, đăng ký kết hôn, bằng đại học, bảng điểm, giấy phép kinh doanh… Xử lý trong ngày, nhận khách vãng lai tại trụ sở Bangkok.",
      },
      {
        q: "Có xử lý hợp pháp hóa cho văn bản sử dụng ở nước ngoài (Apostille/chuỗi lãnh sự) không?",
        a: "Có. Chúng tôi làm trọn gói: Công chứng → Hợp pháp hóa Bộ Ngoại giao Thái → Hợp pháp hóa Đại sứ quán nước đến. Thái Lan gia nhập Công ước Apostille La Haye có hiệu lực từ 28/02/2027, nhiều nước chỉ cần Apostille — tiết kiệm đáng kể thời gian và chi phí. Chúng tôi tư vấn quy trình đúng theo từng quốc gia.",
      },
      {
        q: "Có phục vụ bằng tiếng Việt không?",
        a: "Có. Chúng tôi hỗ trợ 7 ngôn ngữ: Thái, Anh, Trung, Nhật, Hàn, Việt, Indonesia — cho cả công chứng và dịch thuật công chứng.",
      },
      {
        q: "Luật sư có đến khách sạn/chung cư/văn phòng tại Bangkok không?",
        a: "Có. Dịch vụ tận nơi tại toàn bộ 50 quận Bangkok (Sathorn, Silom, Sukhumvit, Asoke, Phrom Phong, Thonglor, Rama 9, Ratchada, Ladprao…). Vui lòng đặt trước ít nhất 1 ngày làm việc qua LINE @Thainotary hoặc +66-94-895-8999.",
      },
    ],
    relatedHeading: "Trang liên quan",
    related: [
      { label: "Trang chủ", to: "/vi" },
      { label: "Khu vực phục vụ", to: "/vi/coverage" },
      { label: "Về chúng tôi", to: "/vi/about" },
      { label: "Liên hệ", to: "/vi/contact" },
    ],
    finalEyebrow: "Sẵn sàng bắt đầu",
    finalHeading: "Tư vấn miễn phí · Báo giá minh bạch · Không phí ẩn",
    badges: ["Được cấp phép bởi Hiệp hội Luật sư Thái Lan", "Trực tiếp nộp BNG+ĐSQ", "Xử lý trong ngày"],
  },

  id: {
    title: "Jasa Notaris Thailand — Terjemahan Tersumpah & Legalisasi | Thai Notary Law",
    description:
      "Kantor pengacara notaris berlisensi di Bangkok. Notaris, legalisir salinan, terjemahan tersumpah 7 bahasa, legalisasi Kemlu Thailand + Kedutaan, visa & hukum korporasi. Hubungi staf kami untuk penawaran harga.",
    keywords:
      "notaris thailand, notary public bangkok, terjemahan tersumpah thailand, legalisasi kedutaan thailand, apostille thailand",
    homeLabel: "Beranda",
    servicesLabel: "Layanan",
    eyebrow: "Bahasa Indonesia · Katalog Lengkap",
    heroTitle: "Notaris Thailand · Terjemahan Tersumpah · Legalisasi Kemlu+Kedutaan",
    heroSubtitle: "16 kategori · 135+ layanan · 6 pengacara notaris berlisensi · 7 bahasa · Selesai hari sama di Bangkok",
    answerSummaryLabel: "Ringkasan jawaban",
    answerSummary:
      "Thai Notary Law & Service Co., Ltd. adalah kantor hukum berlisensi di Bangkok yang menyediakan notaris, legalisir salinan, terjemahan tersumpah, dan legalisasi rantai penuh (Notaris → Kemlu Thailand → Kedutaan, termasuk Apostille berlaku 28 Februari 2027). Keenam pengacara memegang lisensi Notarial Services Attorney dari Lawyers Council of Thailand (Regulasi 2551 B.E.). Biaya layanan diberikan melalui penawaran dari staf, dan kami mengurus pengajuan langsung ke Kemlu (Chaeng Watthana).",
    aboutHeading: "Tentang firma",
    intro:
      "Thai Notary Law & Service Co., Ltd. berkantor pusat di Bangkok (Ekkamai) dengan cabang di Khon Kaen, Udon Thani, dan Nong Khai. Kami melayani WNI di Thailand, perusahaan multinasional, dan perusahaan Thailand yang bertransaksi lintas negara. Tim 6 Notarial Services Attorney memiliki total pengalaman lebih dari 60 tahun, melayani kedutaan besar di Bangkok, universitas luar negeri, pengadilan asing, dan departemen hukum Fortune 500.",
    whyHeading: "Mengapa klien internasional memilih kami",
    why: [
      "6 pengacara notaris berlisensi (Lawyers Council of Thailand, Regulasi 2551 B.E.)",
      "7 bahasa kerja: Thai, Inggris, Mandarin, Jepang, Korea, Vietnam, Indonesia",
      "4 cabang fisik: Bangkok · Khon Kaen · Udon Thani · Nong Khai",
      "Layanan hari sama untuk tindakan notaris standar; ekspres 24 jam tersedia",
      "Pengajuan langsung ke Kemlu (Chaeng Watthana) — klien tidak perlu antri",
      "Harga tetap transparan di situs · Faktur PPN diterbitkan",
      "Layanan on-site di seluruh 50 distrik Bangkok (hotel, kondominium, kantor)",
      "Kemampuan Apostille (berlaku 28 Februari 2027)",
    ],
    feeGuideLabel: "Panduan biaya",
    fees: [
      { label: "Legalisir tanda tangan", price: "Minta penawaran" },
      { label: "Legalisir salinan", price: "Minta penawaran" },
      { label: "Terjemahan tersumpah", price: "Minta penawaran" },
      { label: "Paket Kemlu + Kedutaan", price: "Minta penawaran" },
    ],
    ctaQuote: "Minta penawaran",
    ctaCall: "📞 +66-94-895-8999",
    ctaLine: "💬 LINE @Thainotary",
    categoriesHeading: "Semua 16 kategori layanan",
    faqHeading: "Pertanyaan yang sering diajukan",
    faq: [
      {
        q: "Berapa biaya notaris di Thailand?",
        a: "Biaya layanan kantor kami diberikan melalui penawaran setelah staf memeriksa dokumen, negara tujuan, dan jumlah halaman. Biaya resmi pemerintah ditagih sesuai tarif resmi dan dirinci terpisah. Waktu proses bergantung pada instansi penerima.",
      },
      {
        q: "Apakah notaris Anda diakui oleh kedutaan dan otoritas asing?",
        a: "Ya. Keenam pengacara memegang lisensi Notarial Services Attorney dari Lawyers Council of Thailand (Regulasi 2551 B.E.). Dokumen yang dinotariskan diterima oleh seluruh kedutaan di Bangkok, Kemlu Thailand (Chaeng Watthana), serta pengadilan, universitas, dan bank di luar negeri.",
      },
      {
        q: "Apakah bisa melegalisir salinan paspor, ijazah, KTP di Thailand?",
        a: "Bisa. Legalisir salinan (Certified True Copy) adalah salah satu layanan paling diminati di Bangkok. Meliputi paspor, KTP, akta lahir, akta nikah, ijazah, transkrip, akta pendirian perusahaan. Selesai hari sama, walk-in diterima di kantor pusat Bangkok.",
      },
      {
        q: "Apakah menangani legalisasi untuk penggunaan di luar negeri (Apostille/rantai konsuler)?",
        a: "Ya. Kami menangani rantai penuh: Notaris → Legalisasi Kemlu Thailand → Legalisasi Kedutaan tujuan. Thailand bergabung dengan Konvensi Apostille berlaku 28 Februari 2027, sehingga banyak tujuan cukup dengan Apostille — jauh lebih cepat dan hemat. Kami mengarahkan rantai yang tepat per negara.",
      },
      {
        q: "Apakah tersedia layanan Bahasa Indonesia?",
        a: "Ya. Tim kami mendukung 7 bahasa: Thai, Inggris, Mandarin, Jepang, Korea, Vietnam, dan Indonesia — baik untuk notaris maupun terjemahan tersumpah.",
      },
      {
        q: "Apakah notaris bisa datang ke hotel, kondominium, atau kantor di Bangkok?",
        a: "Bisa. Layanan on-site tersedia di seluruh 50 distrik Bangkok (Sathorn, Silom, Sukhumvit, Asoke, Phrom Phong, Thonglor, Rama 9, Ratchada, Ladprao). Silakan pesan minimal satu hari kerja sebelumnya via LINE @Thainotary atau +66-94-895-8999.",
      },
    ],
    relatedHeading: "Halaman terkait",
    related: [
      { label: "Beranda", to: "/id" },
      { label: "Wilayah layanan", to: "/id/coverage" },
      { label: "Tentang kami", to: "/id/about" },
      { label: "Kontak", to: "/id/contact" },
    ],
    finalEyebrow: "Siap memulai",
    finalHeading: "Konsultasi gratis · Penawaran transparan · Tanpa biaya tersembunyi",
    badges: ["Berlisensi Lawyers Council of Thailand", "Pengajuan langsung Kemlu+Kedutaan", "Selesai hari sama"],
  },
};
